import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { authAPI } from '../services/api';
import { useAuthStore } from '../stores/authStore';

export function SettingsPage() {
  const { user } = useAuthStore();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const changePasswordMutation = useMutation({
    mutationFn: () =>
      authAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword),
    onSuccess: () => {
      setPasswordSuccess('비밀번호가 변경되었습니다.');
      setPasswordError('');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    },
    onError: (error: any) => {
      setPasswordError(
        error.response?.data?.detail || '비밀번호 변경에 실패했습니다.'
      );
      setPasswordSuccess('');
    },
  });

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    changePasswordMutation.mutate();
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">설정</h1>
        <p className="text-gray-600">계정 및 시스템 설정을 관리합니다.</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">프로필</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일
              </label>
              <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600">
                {user?.email || '-'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이름
              </label>
              <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600">
                {user?.name || '-'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                권한
              </label>
              <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600">
                {user?.is_superuser ? '슈퍼 관리자' : '일반 관리자'}
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">비밀번호 변경</h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                현재 비밀번호
              </label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호
              </label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">8자 이상 입력</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호 확인
              </label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                required
              />
            </div>

            {passwordError && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {passwordError}
              </div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-green-50 text-green-600 rounded-lg text-sm">
                {passwordSuccess}
              </div>
            )}

            <button
              type="submit"
              disabled={changePasswordMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {changePasswordMutation.isPending ? '변경 중...' : '비밀번호 변경'}
            </button>
          </form>
        </div>

        {/* System Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">시스템 정보</h2>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">버전</span>
              <span className="font-medium">1.0.0</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">API 서버</span>
              <span className="font-medium">
                {import.meta.env.VITE_API_URL || 'http://localhost:8000'}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-500">벡터 저장소</span>
              <span className="font-medium">ChromaDB</span>
            </div>
            {/*<div className="flex justify-between py-2">
              <span className="text-gray-500">LLM 모델</span>
              <span className="font-medium">GPT-4o-mini</span>
            </div>*/}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">도움말</h2>
          <div className="space-y-4 text-sm text-gray-600">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">테넌트 관리</h3>
              <p>
                테넌트는 각 고객사 또는 프로젝트 단위입니다. 각 테넌트는 고유한 API 키와
                위젯 키를 가지며, 문서와 대화가 격리됩니다.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">문서 학습</h3>
              <p>
                PDF, DOCX, TXT 등의 파일을 업로드하거나 텍스트/Q&A를 직접 입력하여
                챗봇을 학습시킬 수 있습니다. 문서는 자동으로 청크로 분할되어 벡터
                저장소에 저장됩니다.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">위젯 설치</h3>
              <p>
                테넌트 상세 페이지에서 위젯 설치 코드를 복사하여 웹사이트에 붙여넣으면
                챗봇이 활성화됩니다. 허용 도메인을 설정하여 보안을 강화할 수 있습니다.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
